<script setup>
import { computed } from 'vue';

const props = defineProps({
    type: {
        type: String,
        default: 'button',
    },
    textSize: {
        type: String,
        default: 'text-sm',
    },
    color: {
        type: String,
        default: 'bg-[#EDEDED]',
    },
    bgColor: {
        type: String,
    },
    textColor: {
        type: String,
        default: 'text-[#000000]',
    },
    height: {
        type: String,
        default: 'h-[40px]',
    },
    customDisabledColor: {
        type: String,
    },
    width: {
        type: String,
        default: 'w-[140px]',
    },
    lineHeight: {
        type: String,
        default: '25.35px',
    },
    borderColor: {
        type: String,
    },
    paddingX: {
        type: String,
        default: 'px-4',
    },
    paddingY: {
        type: String,
        default: 'py-3',
    },
});

const buttonStyles = computed(() => {
    const styles = {
        lineHeight: props.lineHeight,
    };

    // If bgColor prop is provided, use inline styles
    if (props.bgColor) {
        styles.backgroundColor = props.bgColor;
        styles['--bg-color'] = props.bgColor;
    }

    return styles;
});

// Determine if using bgColor prop
const useInlineStyles = computed(() => !!props.bgColor);
</script>

<template>
    <button
        :class="[
            'secondary-button',
            textSize,
            !useInlineStyles ? color : '',
            textColor,
            height,
            width,
            paddingX,
            paddingY,
            borderColor ? borderColor : '',
            `disabled:${customDisabledColor ?? 'bg-[var(--app-color-ButtonSurfaceDisabled)]'}`,
            useInlineStyles ? 'has-inline-bg' : '',
        ]"
        :style="buttonStyles"
        :type="type"
    >
        <slot />
    </button>
</template>

<style lang="scss" scoped>
.secondary-button {
    @apply rounded font-semibold tracking-widest shadow-sm disabled:text-[var(--app-color-ButtonContentDisabled)] overflow-hidden active:opacity-100 text-ellipsis transition-colors duration-300 flex items-center justify-center;

    letter-spacing: 0;

    &:not(:disabled):hover {
        @apply text-[var(--app-color-ButtonPrimaryContentHover)];
        background: linear-gradient(
                0deg,
                var(--app-color-ButtonSurfaceHover) 0%,
                var(--app-color-ButtonSurfaceHover) 100%
        ),
        var(--app-color-primary);
    }

    &.has-inline-bg:not(:disabled):hover {
        background: color-mix(
                in srgb,
                var(--bg-color) 80%,
                white 20%
        );
    }
}
</style>
